import { captureHealth } from '../lib/captureHealth';
import { noteError, patchDiagnostics } from '../lib/diagnostics';

/**
 * Microphone acquisition. We own all audio — the AR module never touches it.
 *
 * Mic denial must not break the flow. Every function here resolves; a null
 * stream means the clip is silent and the guest continues.
 */

export interface MicResult {
  stream: MediaStream | null;
  granted: boolean;
  /** Set when permission was refused rather than the device being absent. */
  denied: boolean;
}

export interface MicOptions {
  deviceId?: string;
  /**
   * Kiosk open item: a caw is loud and percussive, and aggressive noise
   * suppression on a signage board's built-in mic can gate it to nothing.
   * Default off so we capture the performance; re-test on the real hardware.
   */
  noiseSuppression?: boolean;
  echoCancellation?: boolean;
  autoGainControl?: boolean;
}

export async function requestMicrophone(options: MicOptions = {}): Promise<MicResult> {
  const {
    deviceId,
    noiseSuppression = false,
    echoCancellation = false,
    autoGainControl = false,
  } = options;

  if (!navigator.mediaDevices?.getUserMedia) {
    return { stream: null, granted: false, denied: false };
  }

  const audio: MediaTrackConstraints = {
    noiseSuppression,
    echoCancellation,
    autoGainControl,
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  };

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio, video: false });
    const track = stream.getAudioTracks()[0];
    patchDiagnostics({
      micGranted: true,
      micDeviceId: track?.getSettings().deviceId ?? deviceId ?? null,
      micLabel: track?.label ?? null,
    });
    return { stream, granted: true, denied: false };
  } catch (err) {
    const name = err instanceof DOMException ? err.name : '';
    const denied = name === 'NotAllowedError' || name === 'SecurityError';
    // Not an error we surface to the guest — the flow continues silent.
    noteError('mic', err);
    patchDiagnostics({ micGranted: false });
    return { stream: null, granted: false, denied };
  }
}

/**
 * Builds the recording stream: video from the AR canvas, audio from the mic if
 * we have it. A missing mic track is simply an absent track, not a failure.
 */
export function buildCaptureStream(
  canvas: HTMLCanvasElement,
  micStream: MediaStream | null,
  fps = 30,
): MediaStream {
  const video = canvas.captureStream(fps);
  const tracks: MediaStreamTrack[] = [...video.getVideoTracks()];
  captureHealth.setCaptureTrack(tracks[0] ?? null);
  const audioTrack = micStream?.getAudioTracks()[0];
  if (audioTrack && audioTrack.readyState === 'live') tracks.push(audioTrack);
  return new MediaStream(tracks);
}

/** Stops mic tracks. The AR module owns and stops its own camera tracks. */
export function releaseMicrophone(stream: MediaStream | null): void {
  stream?.getAudioTracks().forEach((t) => t.stop());
}
